<template>
	<div class="shipment-search">
		<div class="container ">
			<!-- Header -->
			<div class="page-header flex-center">
				<h1 class="page-title">{{ $t('Leads Order') }}</h1>
				<div class="page-header-right text-right ml-a">
					<!-- Date Filter -->
					<daterangepicker
						:presets="filterDatePresets"
						:start="since"
						:end="until"
						:compact="isDeviceMobile"
						@selected="setFilterDate"
						ref="datePicker"
					>
					</daterangepicker>
				</div>
			</div>

			<!-- Overviews -->
			<div class="submissions-summaries">
				<overviews
				:overview="overview"
				:layout="'medium'"
				:items="overviewItems.primary"
				:capabilities="user.capabilities"
				:selected-overview = selectedOverview
				:source="'leads-order'"
				:is-fetching="isFetchingStatistic"
			>
			</overviews>
			</div>

			<!-- List -->
			<div class="main-table">
				<!-- Table Actions -->
				<div class="row mb-4">
					<div class="col-md-6">
							<div class="row ml-1">
								<div class="col-md-6 p-0 m-0">
									<select v-model="filterCategory" id="" class="filter-category form-control">
										<option value="reference_id">Reference id</option>
										<option value="product_name">Product</option>
										<option value="supplier_name">Supplier</option>
										<option value="seller_name">Seller</option>
									</select>
								</div>
								<div class="col-md-6 p-0 m-0">
									<input
									type="text"
									class="form-control"
									:placeholder="placeholder"
									v-focus
									@input="onKeywordChange"
									v-model="keyword"
									>
								</div>
							</div>
					</div>
					<div class="col-md-6 d-flex align-items-center justify-content-end flex-wrap mt-2 mt-md-0">
						<div class="mr-2">
							<select name="" id="" class="form-control" v-model="status">
								<option value="">Tampilkan semua</option>
								<option value="pending">Pending</option>
								<option value="failed">Failed</option>
							</select>
						</div>
						<!-- Export -->
						<div class="dropdown">
							<button
								class="btn btn-light flex-center"
								@click="exportLeadsOrder()"
								:class="{ 'is-loading': isExporting }"
							>
								<i class="material-icons">save_alt</i>
								<span class="mr-2">{{ $t('Export') }}</span>
							</button>
						</div>
					</div>
				</div>

				<div class="submissions-list table-responsive b-0" id="orders-list">
					<table class="table table-primary table-striped table-hover table--small submissions-list-table" id="orders-list-table">
						<thead>
							<tr>
								<th width="5%">No.</th>
								<th class="text-left">Reference ID</th>
								<th class="" width="5%">Produk</th>
								<th class="text-left" width="25%"></th>
								<th class="text-left">Variations</th>
								<th class="text-left">Quantity</th>
								<th class="text-left">Status</th>
								<th class="">Price</th>
								<th class="" >Order at</th>
								<th class="text-left" width="20%">Supplier</th>
								<th class="text-left" width="20%">Seller</th>
								<th class="text-left" width="10%">Alamat</th>
							</tr>
						</thead>
						<tbody>
							<leads-order-search-row
								v-for="(order, index) in orders"
								:key="index"
								:index="index"
								:order="order"
								@detail="showDetailOrder"
							/>
						</tbody>
					</table>

					<div
						class="list-empty table-empty text-center"
						v-if="orders.length === 0 && !isSearching">
						<i class="material-icons mr-2">error_outline</i>
						<span>No Orders Found</span>
					</div>

					<!-- Loader -->
					<div class="my-3 p-3 text-center" v-if="isSearching">
						<div class="is-loading dark large"></div>
					</div>

					<div class="table-vertical-scroll hidden-sm" id="orders-list-vertical-scroll">
						<div class="table-vertical-scroll-content" id="orders-list-vertical-scroll-content"></div>
					</div>
				</div>
			</div>
		</div>
	<!-- Modal detail order -->
	<modal
		:class="{ 'is-printing': isPrinting }"
		class="order-details-modal"
		size="medium"
		@close="isShowDetailOrder = false"
		v-if="isShowDetailOrder && selectedSubmission"
	>
		<div slot="header">
			<h3><b>Order Details</b></h3>
		</div>
		<div slot="body">
			<invoice
				:store="user.store"
				:timezone="user.timezone"
				:order="selectedSubmission"
				:payment-visible="isPaymentVisible"
				:courier-visible="isCourierVisible"
			>
			</invoice>
		</div>
		<template slot="footer">
			<checkbox-toggle
				label="Show Payment Options"
				v-model="isPaymentVisible"
			>
			</checkbox-toggle>
			<checkbox-toggle
				:label="$t('_.hideCourier')"
				v-model="isCourierVisible"
			>
			</checkbox-toggle>
			<button class="btn btn-light ml-a" @click="isShowDetailOrder = false">
				{{ $t('Close') }}
			</button>
			<button
				class="btn btn-primary"
				@click="printInvoice"
				:class="{ 'is-printing': isPrinting }"
			>
				<i class="material-icons">cloud_download</i>
				<span>Download Invoice</span>
			</button>
		</template>
	</modal>
	</div>
</template>

<script>
import html2canvas from 'html2canvas';
import * as jsPDF from 'jspdf';
import { mapGetters, mapActions } from 'vuex';
import {
	duplicateVar,
	isMobile,
	getDateRange,
	downloadFile,
	makePDF,
	generateDateRange,
} from '@/lib/helper';
import adminApi from '@/api/admin';
import Daterangepicker from '@/components/Daterangepicker.vue';
import Overviews from '@/components/Overviews.vue';
import LeadsOrderSearchRow from '@/components/LeadsOrderSearchRow.vue';
import Modal from '@/components/Modal.vue';
import Invoice from '@/components/Invoice.vue';
import CheckboxToggle from '@/components/CheckboxToggle.vue';

const defaultFilterDate = getDateRange();
export default {
	name: 'AdminLeadsOrder',
	props: {
		type: {
			type: String,
			default: 'shipment',
		},
	},
	components: {
		Daterangepicker,
		Overviews,
		LeadsOrderSearchRow,
		Modal,
		Invoice,
		CheckboxToggle,
	},
	data() {
		return {
			page: 1,
			limit: 100,
			sort_by: 'created_at',
			sort: 'desc',
			isAll: false,
			keyword: '',
			since: null,
			until: null,
			isDeleted: false,
			isDeviceMobile: false,
			isSearching: false,
			isFetchingStatistic: false,
			orders: [],
			isShowModalProduct: false,
			overviewItems: {
				primary: [
					'leads_order_total',
					'leads_order_failed',
				],
			},
			filterDate: duplicateVar(defaultFilterDate),
			overview: {
				leads_order_total: 0,
				leads_order_failed: 0,
			},
			isExporting: false,
			isShowDetailOrder: false,
			isPrinting: false,
			filterCategory: 'reference_id',
			status: '',
		};
	},
	methods: {
		...mapActions({
			fetchUser: 'fetchUser',
		}),
		generateDateRange,
		clearFilter() {
			this.selectedSeller = null;
			this.selectedSupplier = null;
			this.selectedPaymentMethod = null;
			this.selectedCourier = null;
			this.filterReturnAtSince = null;
			this.filterReturnAtUntil = null;
			this.filterDate = getDateRange();

			this.fetchData(true);
			this.fetchOverviews();
		},
		printInvoice() {
			this.isPrinting = true;
			const orderId = this.selectedSubmission.order_id;
			const timestamp = new Date().getTime();
			const name = `Invoice-${orderId}-${timestamp}`;
			const callback = () => {
				this.isPrinting = false;
			};
			setTimeout(() => {
				makePDF(document, 'invoice', html2canvas, jsPDF, name, callback);
			}, 300);
		},
		showDetailOrder(submission, event) {
			event.preventDefault();

			this.selectedSubmission = submission;
			this.isShowDetailOrder = true;
		},
		resetItems() {
			this.orders = [];
			this.isAll = false;
			this.page = 1;
		},
		fetchData(reset = true) {
			// if (this.keyword.trim() === '') {
			// 	return;
			// }
			this.isSearching = true;

			if (reset) {
				this.resetItems();
			}

			const params = this.fetchParams;

			const callback = (response) => {
				const orders = response.data;
				// this.orders = orders;
				this.isSearching = false;

				if (orders.length > 0) {
					this.page++;
					this.orders = this.orders.concat(orders);
				} else {
					this.isAll = true;
				}
				this.optionSeller = [];
				this.optionSupplier = [];
				this.isSearchingSupplier = false;
				this.isSearchingSeller = false;
			};
			const errorCallback = () => {
				this.isSearching = false;
			};
			adminApi.getLeadsOrder(params, callback, errorCallback);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 100;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isSearching && !this.isAll) {
					this.fetchData(false);
				}
			}
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				const keyword = this.keyword ? this.keyword.trim() : '';
				const query = duplicateVar(this.$route.query);
				if (keyword !== '') {
					query.q = keyword;
				} else {
					delete query.q;
				}

				if (this.filterCategory) {
					query.filter_category = this.filterCategory;
				} else {
					delete query.filter_category;
				}

				const params = { query };
				this.$router.push(params);
				self.fetchData();
			}, 1000);
		},
		checkListTable() {
			const ordersList = document.getElementById('orders-list');
			if (ordersList) {
				const ordersListVerticalScroll = document.getElementById('orders-list-vertical-scroll');
				const documentHeight = document.documentElement.clientHeight;
				const scrollTop = document.documentElement.scrollTop;
				const ordersListHeight = ordersList.offsetHeight;
				const offsetTop = ordersList.offsetTop;
				const offsetBottom = offsetTop + ordersListHeight;
				const scrollBottom = offsetBottom - scrollTop;
				const topTreshold = 100;
				const scrollTreshold = offsetTop - (documentHeight - topTreshold);
				const display = scrollTop >= scrollTreshold && scrollBottom >= documentHeight ? 'block' : 'none';
				ordersListVerticalScroll.style.display = display;
			}
		},
		onDocumentScroll(event) {
			if (event.target === document) {
				this.checkListTable();
			} else if (event.target.id === 'orders-list') {
				const element = event.target;
				const scrollLeft = element.scrollLeft;
				const ordersListVerticalScroll = document.getElementById('orders-list-vertical-scroll');
				ordersListVerticalScroll.scrollLeft = scrollLeft;
			} else if (event.target.id === 'orders-list-vertical-scroll') {
				const element = event.target;
				const scrollLeft = element.scrollLeft;
				const ordersList = document.getElementById('orders-list');
				ordersList.scrollLeft = scrollLeft;
			}
		},
		setVerticalScroll() {
			const ordersList = document.getElementById('orders-list');
			if (ordersList) {
				const ordersListTable = document.getElementById('orders-list-table');
				const ordersListVerticalScroll = document.getElementById('orders-list-vertical-scroll');
				const ordersListVerticalScrollContent = document.getElementById('orders-list-vertical-scroll-content');
				const listWidth = ordersList.offsetWidth;
				const listLeft = ordersList.offsetLeft;
				const tableWidth = ordersListTable.offsetWidth;
				ordersListVerticalScroll.style.width = `${listWidth}px`;
				ordersListVerticalScroll.style.left = `${listLeft}px`;
				ordersListVerticalScrollContent.style.width = `${tableWidth}px`;
			}
		},
		resetFilter() {
			this.selectedStatus = [];
			this.filterDeliveryStatus = '';
			this.filterIsManifested = false;
			this.isDeleted = null;
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const since = this.moment(filterDate.start).format('YYYY-MM-DD');
			const until = this.moment(filterDate.end).format('YYYY-MM-DD');
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.applyFilter();
		},
		fetchOverviews() {
			this.isFetchingStatistic = true;
			const params = this.fetchParams;

			const callback = (response) => {
				const statistic = response.data;
				this.overview = statistic;
				this.isFetchingStatistic = false;
			};
			const errorCallback = (e) => {
				console.log(e);
				this.isFetchingStatistic = false;
			};
			adminApi.statisticLeadsOrder(params, callback, errorCallback);
		},
		applyFilter() {
			this.fetchOverviews();
			this.fetchData();
			this.isShowFilter = false;
		},
		exportLeadsOrder() {
			this.isExporting = true;
			const params = this.fetchParams;

			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					const message = 'No transactions with the selected type';
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Export Transactions',
						text: message,
					});
				}
				this.isExporting = false;
			};
			const errorCallback = () => {
				this.isExporting = false;
			};
			adminApi.exportLeadsOrder(params, callback, errorCallback);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			// overview: 'overview',
			filterDatePresets: 'filterDatePresets',
			isAdmin: 'isAdmin',
			isUserCan: 'isUserCan',
		}),
		selectedOverview() {
			let selectedOverview = '';

			if (this.status === 'pending') {
				selectedOverview = 'leads_order_total';
			} else if (this.status === 'failed') {
				selectedOverview = 'leads_order_failed';
			} else {
				return '';
			}

			return selectedOverview;
		},
		placeholder() {
			let placeholder = '';
			if (this.filterCategory === 'reference_id') {
				placeholder = 'Cari berdasarkan Reference Id';
			}
			if (this.filterCategory === 'product_name') {
				placeholder = 'Cari berdasarkan Nama Product';
			}
			if (this.filterCategory === 'supplier_name') {
				placeholder = 'Cari berdasarkan Nama Supplier';
			}
			if (this.filterCategory === 'seller_name') {
				placeholder = 'Cari berdasarkan Nama Seller';
			}

			return placeholder;
		},
		isFilterOn() {
			return this.selectedSeller || this.selectedSupplier || this.selectedCourier || this.selectedPaymentMethod || this.filterReturnAtSince || this.filterReturnAtUntil;
		},
		fetchParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);
			const keyword = this.keyword.trim();
			const isDeleted = this.isDeleted;
			const filterCategory = this.filterCategory;
			const status = this.status !== '' ? this.status : null;

			const params = {
				page: this.page,
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				since: startDate,
				until: endDate,
				keyword,
			};

			if (status !== '') {
				params.status = status;
			}

			if (isDeleted !== null) {
				params.is_deleted = isDeleted;
			}

			if (filterCategory !== '' && keyword !== '') {
				params.filter_category = filterCategory;
			}

			return params;
		},
		keywordQuery() {
			const keyword = this.$route?.query?.q ?? '';
			return keyword;
		},
	},
	watch: {
		status() {
			this.fetchData(true);
		},
	},
	created() {
		this.isDeviceMobile = isMobile();
		document.addEventListener('scroll', this.onDocumentScroll, true);
		window.addEventListener('resize', this.checkListTable, true);

		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
		this.fetchData();
		this.fetchOverviews();
	},
	destroyed() {
		document.removeEventListener('scroll', this.onDocumentScroll, true);
		window.removeEventListener('resize', this.checkListTable, true);
		window.removeEventListener('scroll', this.handleScroll);
	},
	updated() {
		this.$nextTick(() => {
			this.setVerticalScroll();
			this.checkListTable();
		});
	},
};
</script>

<style lang="scss">
.filter-category{
	border-radius: 0px;
}
</style>
